import { styled } from '@mui/material/styles';
import { Box, Button, Card } from '@mui/material';
import { BRAND_COLORS, SEMANTIC_COLORS, alpha } from '../../styles/theme';

export const FileGridContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: '0.75rem',
  padding: '1rem',
});

export const FileCard = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isFolder' && prop !== 'selected' && prop !== 'dropActive' && prop !== 'cut',
})<{ isFolder?: boolean; selected?: boolean; dropActive?: boolean; cut?: boolean }>(
  ({ isFolder, selected, dropActive, cut }) => ({
    // Explorer "cut" look: dim the source card until the paste (or Escape) clears it.
    opacity: cut ? 0.45 : undefined,
    backgroundColor: dropActive
      ? alpha(BRAND_COLORS.yellow.main, 0.25)
      : selected
        ? alpha(BRAND_COLORS.forestGreen.main, 0.12)
        : '#f7fafc',
    border: `${dropActive ? 2 : 1}px ${dropActive ? 'dashed' : 'solid'} ${
      dropActive ? BRAND_COLORS.yellow.main : selected ? BRAND_COLORS.forestGreen.main : '#e2e8f0'
    }`,
    color: '#4a5568',
    padding: '0.75rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Industry, sans-serif',
    fontSize: '0.9rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    textAlign: 'left',
    boxShadow: selected
      ? `0 0 0 1px ${BRAND_COLORS.forestGreen.main}, 0 4px 12px rgba(0, 0, 0, 0.15)`
      : '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    minWidth: '0',
    position: 'relative',
    // Prevent the OS text-selection caret while marquee/multi-selecting.
    userSelect: 'none',
    minHeight: isFolder ? '60px' : '100px',
    '&:hover': {
      backgroundColor: selected ? alpha(BRAND_COLORS.forestGreen.main, 0.16) : '#edf2f7',
      borderColor: BRAND_COLORS.forestGreen.main,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  })
);

export const FileHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  minWidth: 0,
});

export const FolderName = styled('span')({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  wordBreak: 'break-word',
  minWidth: 0,
  flex: 1,
  fontSize: '0.9rem',
  fontWeight: 500,
  lineHeight: 1.3,
});

export const FileMetadata = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  fontSize: '0.75rem',
  color: SEMANTIC_COLORS.text.secondary,
  marginTop: '4px',
});

export const MetadataRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

// The chip row under a card's metadata. Wraps rather than overflowing: the
// card is as narrow as 180px, and three chips do not fit on one line (#1692).
export const ChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
});

export const FileIcon = styled(Box)({
  width: '20px',
  height: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 600,
  flexShrink: 0,
  fontFamily: 'Industry, sans-serif',

  '&.folder': {
    fontSize: '16px',
  },

  '&.file': {
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    color: 'white',
  },

  '&.custom': {
    color: SEMANTIC_COLORS.text.secondary,
  },

  '&.csv': {
    background: 'linear-gradient(135deg, #10b981, #047857)',
    color: 'white',
  },
});

export const BreadcrumbContainer = styled(Box)({
  padding: '1rem',
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.02),
  borderRadius: '8px',
  marginBottom: '1rem',
});

export const ActionButton = styled(Button)({
  backgroundColor: BRAND_COLORS.forestGreen.main,
  color: BRAND_COLORS.white.main,
  '&:hover': {
    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
  },
});

// Rubber-band marquee rectangle — positioned `fixed` in viewport coords.
export const MarqueeBox = styled(Box)({
  position: 'fixed',
  zIndex: 1300,
  pointerEvents: 'none',
  border: `1px solid ${BRAND_COLORS.forestGreen.main}`,
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.12),
  borderRadius: '2px',
});

// "N items" chip shown in the dnd-kit DragOverlay while dragging a selection.
export const DragCountChip = styled(Box)({
  backgroundColor: BRAND_COLORS.forestGreen.main,
  color: BRAND_COLORS.white.main,
  borderRadius: '6px',
  padding: '6px 12px',
  fontFamily: 'Industry, sans-serif',
  fontSize: '0.85rem',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
  whiteSpace: 'nowrap',
});

export const UploadZone = styled(Card)({
  marginBottom: '1rem',
  backgroundColor: alpha(BRAND_COLORS.yellow.main, 0.02),
  border: `1px solid ${alpha(BRAND_COLORS.yellow.main, 0.2)}`,
});

export const AnalysisResultCard = styled(Card)({
  marginTop: '1rem',
  backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
  border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.2)}`,
});
